/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import FormBox from '../../common/FormBox/FormBox';
import { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import ToggleButtonGroup from '../../common/ToggleButtonGroup/ToggleButtonGroup';
import { FaTrash } from 'react-icons/fa';
import ReactGA from 'react-ga4';
import api from 'api';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { chosenCountryName, countryNameOptions, relativesQuestionLabelKey } from 'utils/countries';
import AddEntryFooter from '../AddEntryFooter';
import * as S from '../Forms.styled';
import { useAuth } from 'contexts/ProtectedRoute';
import { useParams } from 'react-router-dom';
import { isSalesPersonRole } from 'data/constants/roles';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useTranslation } from 'react-i18next';
import { fetchOptionsReturn, fetchOptions } from './utils';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { formatDateToYMD, isFullDateFormat, parseStoredDate } from 'utils/fullDateFormat';

let ages = new Array(112).fill(0).map((_, i) => String(i + 1));
ages = ['', ...ages];

interface Entry {
    relation: string | null;
    country: string | null;
    region: string | null;
    residency_status: string | null;
    age: string;
    marital_status: string;
    residence_since: string;
    nationality: string;
    country_of_birth: string;
}

const initEntry: Entry = {
    relation: '',
    country: '',
    region: '',
    residency_status: '',
    age: '',
    marital_status: '',
    residence_since: '',
    nationality: '',
    country_of_birth: '',
};

interface Msg {
    relation: string;
    country: string;
    region: string;
    residency_status: string;
    age: string;
    marital_status: string;
    residence_since: string;
    nationality: string;
    country_of_birth: string;
}

const initMsg: Msg = {
    relation: '',
    country: '',
    region: '',
    residency_status: '',
    age: '',
    marital_status: '',
    residence_since: '',
    nationality: '',
    country_of_birth: '',
};

const initResidenceStatus = [
    '',
    'Citizen',
    'Permanent resident',
    'Temporary resident',
    'Study permit',
];
// const statusesDefault = ['', 'Permanent resident', 'Temporary resident', 'Study permit'];

const RelativesAbroadForm = () => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { codes, hasSelfServeMarket, has } = useEnabledCountries();
    const tableName = 'RelativeAbroad';
    const [data, setData] = useState<Entry[]>([]);
    const [validationMsg, setValidationMsg] = useState<Msg[]>([]);
    const [hasRelative, setHasRelative] = useState<boolean>(false);
    const [residenceStatus, setResidenceStatus] = useState<string[][]>([initResidenceStatus]);
    const { role } = useAuth();
    const params = useParams();
    const candidate_id = Number(params.id);
    const blankRelative = (): Entry => ({
        ...initEntry,
        country: chosenCountryName(codes) ?? '',
    });

    const [isUserData, setIsUserData] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [options, setOptions] = useState({
        relationOptions: [''],
        regionOptions: {} as { [key: string]: string[] },
    });

    useEffect(() => {
        loadOptions();
    }, []);

    useEffect(() => {
        if (isUserData) {
            setIsLoading(false);
        }
    }, [isUserData]);

    const loadOptions = async () => {
        const [relations, regions] = await Promise.all([
            fetchOptionsReturn(api?.assessment_options.getFamilyRelationOptions),
            fetchOptionsReturn(api?.assessment_options.getRegionOptions),
        ]);

        setOptions({
            relationOptions: relations || [''],
            regionOptions: regions || {},
        });
    };

    const isSalesPerson = isSalesPersonRole(role);
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    // const [residenceStatus, setResidenceStatus] = useState<string[]>(statusesDefault);

    // useEffect(() => {
    //     const loadStatuses = async () => {
    //         const res = await api.staticResources.getResidenceStatusList('Australia');
    //         const statuses = statusesDefault.concat(res.data);
    //         setResidenceStatus(statuses);
    //     };
    //     loadStatuses();
    // }, []);

    const loadData = async (data: Entry[]) => {
        // loadOptions();
        if (data && data.length | Object.keys(data).length) {
            const tmp = new Array(data.length).fill(initMsg);
            setValidationMsg(tmp);
            setData(data);

            if (data.length > 0) {
                setHasRelative(true);
                const tempResidence = new Array(data.length).fill(initResidenceStatus);
                setResidenceStatus(tempResidence);
                for (let i = 0; i < data.length; i++) {
                    const _country = data[i].country;
                    if (_country) {
                        const res = await api.staticResources.getResidenceStatusList(_country);
                        tempResidence[i] = [...res.data];
                        setResidenceStatus(tempResidence);
                    }
                }
            }
        }
        if (data == null || data) {
            setIsUserData(true);
        } else {
            setIsUserData(false);
        }
    };

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname + '/' + tableName}`,
            title: 'RelativesAbroadForm',
        });
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            setIsLoading(true);
            setIsUserData(false);
            setData([]);
            isSalesPerson
                ? api.tableInput.fetchCandidateInputSales(loadData, tableName, candidate_id)
                : api.tableInput.fetchCandidateInput(loadData, tableName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidate_id, isAuthenticated]);

    // FIELD VALIDATION
    const validate = () => {
        let validated = true;
        const temp = [...validationMsg];
        for (let i = 0; i < data.length; i++) {
            for (const [key, value] of Object.entries(data[i])) {
                let msg = '';
                if (
                    key === 'marital_status' ||
                    key === 'residence_since' ||
                    key === 'nationality' ||
                    key === 'country_of_birth'
                )
                    continue;
                if (value === '') {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                }
                temp[i] = { ...temp[i], [key]: msg };
            }
        }
        setValidationMsg(temp);

        return validated;
    };

    const validateSingleField = (
        idx: number,
        field: string,
        value: string | boolean | null | number
    ) => {
        const temp = [...validationMsg];
        let msg = '';
        if (value === '' || value === null) {
            msg = t('requiredField', { lng: language });
        }
        temp[idx] = { ...temp[idx], [field]: msg };
        setValidationMsg(temp);
    };

    // const handleUpdate = async (idx: number, field: string, value: string | any) => {
    //      let temp = [...data];
    //   //  let temp: any;
    //     if (field === 'country_of_residence') {
    //         // temp = { ...data[idx], [field]: value, region_of_residence: '', residency_status: '' };
    //         const res = await api.staticResources.getResidenceStatusList(value);
    //         // let tempResidence = [...residenceStatus];
    //         // tempResidence[idx] = {}

    //       //  setResidenceStatus(tempResidence);
    //         // setSpouseResidenceStatus(res.data);
    //     } else {
    //         // temp = { ...data[idx], [field]: value };
    //         temp = { ...data[idx], [field]: value };
    //     }

    //     // temp[idx] = { ...data[idx], [field]: value };
    //     setData(temp);
    // };

    const handleUpdate = async (idx: number, field: string, value: string | any) => {
        const temp = [...data];
        if (field === 'country') {
            temp[idx] = {
                ...data[idx],
                [field]: value,
                region: '',
                residency_status: '',
            };
            const res = await api.staticResources.getResidenceStatusList(value);
            const tempResidence = [...residenceStatus];
            tempResidence[idx] = [...res.data];
            setResidenceStatus(tempResidence);
        } else {
            temp[idx] = { ...data[idx], [field]: value };
        }
        setData(temp);
    };

    const handleUpdateHasRelative = (value: string) => {
        if (value === t('yes', { lng: language })) {
            setHasRelative(true);
            setData([blankRelative()]);
            setValidationMsg([initMsg]);
        } else if (value === t('no', { lng: language })) {
            setHasRelative(false);
            setData([]);
            setValidationMsg([]);
        }
    };

    const handleAddEntry = () => {
        if (data.length > 10) return;
        const newEntry: Entry = blankRelative();

        const temp: Entry[] = [...data, newEntry];
        setData(temp);

        const tempMsg: Msg[] = [...validationMsg, initMsg];
        setValidationMsg(tempMsg);

        const tempResidence = [
            ...residenceStatus,
            ['Citizen', 'Permanent resident', 'Temporary resident', 'Study permit'],
        ];
        setResidenceStatus(tempResidence);
    };

    const handleDeleteEntry = (idx: number) => {
        const temp: Entry[] = [...data];
        temp.splice(idx, 1);
        setData(temp);
        const tempMsg: Msg[] = [...validationMsg];
        tempMsg.splice(idx, 1);
        setValidationMsg(tempMsg);
    };
    const handleDateUpdateMe = (idx: number, field: string, newDate: Date | null) => {
        if (newDate === null) {
            // residence_since is optional — clear without required error
            const tempMsg = [...validationMsg];
            tempMsg[idx] = { ...tempMsg[idx], [field]: '' };
            setValidationMsg(tempMsg);
            const temp = [...data];
            temp[idx] = { ...data[idx], [field]: '' };
            setData(temp);
            return;
        }
        if (!(newDate instanceof Date) || isNaN(newDate.getTime())) {
            const tempMsg = [...validationMsg];
            tempMsg[idx] = { ...tempMsg[idx], [field]: 'Invalid date format' };
            setValidationMsg(tempMsg);
            return;
        }

        const extractDateFormat = formatDateToYMD(newDate);

        if (!isFullDateFormat(newDate)) {
            // Incomplete year while typing — keep month visible
            const tempMsg = [...validationMsg];
            tempMsg[idx] = { ...tempMsg[idx], [field]: 'Invalid date format' };
            setValidationMsg(tempMsg);
            const temp = [...data];
            temp[idx] = { ...data[idx], [field]: extractDateFormat };
            setData(temp);
            return;
        }

        const tempMsg = [...validationMsg];
        tempMsg[idx] = { ...tempMsg[idx], [field]: '' };
        setValidationMsg(tempMsg);
        validateSingleField(idx, field, extractDateFormat);
        const temp = [...data];
        temp[idx] = { ...data[idx], [field]: extractDateFormat };
        setData(temp);
    };

    const [nationalities, setNationalities] = useState<string[]>([]);
    const [allCountryOptions, setAllCountryOptions] = useState<string[]>([]);

    const getNationalityOptions = () =>
        fetchOptions(api?.assessment_options.getNationalityOptions, setNationalities);
    const getCountryOptions = () =>
        fetchOptions(api?.assessment_options.getCountryOptions, setAllCountryOptions);

    useEffect(() => {
        getNationalityOptions();
        getCountryOptions();
    }, []);

    const nationalityOptions = nationalities.length > 0 ? nationalities : allCountryOptions;

    return (
        <>
            {isLoading ? (
                <S.LoadingContainer>
                    <CircularProgress color="primary" />
                </S.LoadingContainer>
            ) : (
                <FormBox validation={validate} data={[data]} tableNames={[tableName]}>
                    <S.Title>{t('relativesAbroad', { lng: language })}</S.Title>

                    {/* <S.GridSection> */}
                    <ToggleButtonGroup
                        customStyle={true}
                        options={[t('yes', { lng: language }), t('no', { lng: language })]}
                        name="have_relative"
                        label={t(relativesQuestionLabelKey(codes), { lng: language })}
                        value={
                            hasRelative ? t('yes', { lng: language }) : t('no', { lng: language })
                        }
                        onChange={(_: string, value: string) => handleUpdateHasRelative(value)}
                    />
                    {/* </S.GridSection> */}

                    {hasRelative &&
                        data.map((item, i) => {
                            return (
                                <S.EntryCard key={i}>
                                    <div>
                                        <S.SubTitle>
                                            <>{`${t('relative', { lng: language })} ${i + 1}`}</>
                                            <S.DeleteButton>
                                                <button
                                                    onClick={() => {
                                                        handleDeleteEntry(i);
                                                    }}
                                                >
                                                    <FaTrash />
                                                </button>
                                            </S.DeleteButton>
                                        </S.SubTitle>
                                    </div>
                                    <S.FlexColumn>
                                        <div>
                                            <S.GridSection>
                                                <Autocomplete
                                                    id="Relation"
                                                    value={item.relation}
                                                    options={options.relationOptions}
                                                    onChange={(_, value: string | null) => {
                                                        handleUpdate(i, 'relation', value);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('relation', {
                                                                lng: language,
                                                            })}`}
                                                            error={validationMsg[i].relation !== ''}
                                                            helperText={validationMsg[i].relation}
                                                        />
                                                    )}
                                                />{' '}
                                                <Autocomplete
                                                    disablePortal
                                                    id="country"
                                                    value={item.country}
                                                    options={countryNameOptions(
                                                        codes,
                                                        item.country
                                                    )}
                                                    onChange={(_, value: string | null) =>
                                                        handleUpdate(i, 'country', value)
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('country', {
                                                                lng: language,
                                                            })}`}
                                                            error={validationMsg[0].country !== ''}
                                                            helperText={validationMsg[i].country}
                                                        />
                                                    )}
                                                />
                                                {hasSelfServeMarket && (
                                                    <Autocomplete
                                                        disablePortal
                                                        id="country_of_birth"
                                                        value={item.country_of_birth}
                                                        options={allCountryOptions}
                                                        onChange={(_, value: string | null) =>
                                                            handleUpdate(
                                                                i,
                                                                'country_of_birth',
                                                                value
                                                            )
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={`${t('countryOfBirth', {
                                                                    lng: language,
                                                                })}`}
                                                                error={
                                                                    validationMsg[i]
                                                                        .country_of_birth !== ''
                                                                }
                                                                helperText={
                                                                    validationMsg[i]
                                                                        .country_of_birth
                                                                }
                                                            />
                                                        )}
                                                    />
                                                )}
                                                {has('UK') && (
                                                    <>
                                                        <ToggleButtonGroup
                                                            options={[
                                                                t('married', { lng: language }),
                                                                t('single', { lng: language }),
                                                            ]}
                                                            name="marital_status"
                                                            label={t('maritalStatus', {
                                                                lng: language,
                                                            })}
                                                            value={item.marital_status}
                                                            // onChange={handleUpdateMe}
                                                            onChange={(_, value: string | null) => {
                                                                handleUpdate(
                                                                    i,
                                                                    'marital_status',
                                                                    value
                                                                );
                                                            }}
                                                            error={validationMsg[i].marital_status}
                                                        />
                                                        <Autocomplete
                                                            disablePortal
                                                            value={item.nationality}
                                                            id="nationality"
                                                            options={nationalityOptions}
                                                            onChange={(_, value: string | null) =>
                                                                handleUpdate(
                                                                    i,
                                                                    'nationality',
                                                                    value
                                                                )
                                                            }
                                                            // onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                                            //     validate('nationality', e.target.value, undefined, 0, 'spouse');
                                                            // }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label={`${t('nationality', { lng: language })}`}
                                                                    error={
                                                                        validationMsg[i]
                                                                            ?.nationality !== ''
                                                                    }
                                                                    helperText={
                                                                        validationMsg[i]
                                                                            ?.nationality
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </>
                                                )}
                                                <Autocomplete
                                                    disablePortal
                                                    id="region"
                                                    value={item.region}
                                                    options={
                                                        options.regionOptions[
                                                            item.country as string
                                                        ] || []
                                                    }
                                                    onChange={(_, value: string | null) =>
                                                        handleUpdate(i, 'region', value)
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('region', {
                                                                lng: language,
                                                            })}`}
                                                            error={validationMsg[i].region !== ''}
                                                            helperText={validationMsg[i].region}
                                                        />
                                                    )}
                                                />
                                                <Autocomplete
                                                    id="residency_status"
                                                    value={item.residency_status}
                                                    options={residenceStatus[i]}
                                                    onChange={(_, value: string | null) => {
                                                        handleUpdate(i, 'residency_status', value);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('residenceStatus', {
                                                                lng: language,
                                                            })}`}
                                                            error={
                                                                validationMsg[i]
                                                                    .residency_status !== ''
                                                            }
                                                            helperText={
                                                                validationMsg[i].residency_status
                                                            }
                                                        />
                                                    )}
                                                />
                                                <LocalizationProvider
                                                    sx={{
                                                        '& .MuiInputBase-root': {
                                                            height: 30,
                                                        },
                                                    }}
                                                    dateAdapter={AdapterDateFns}
                                                >
                                                    <DesktopDatePicker
                                                        views={['year', 'month']}
                                                        maxDate={new Date()}
                                                        label={`${t('residenceStatusSince', { lng: language })}`}
                                                        format="MM/yyyy"
                                                        value={parseStoredDate(
                                                            item.residence_since
                                                        )}
                                                        onChange={(date: Date | null) => {
                                                            handleDateUpdateMe(
                                                                i,
                                                                'residence_since',
                                                                date
                                                            );
                                                        }}
                                                        slotProps={{
                                                            field: { clearable: true },
                                                            textField: {
                                                                error:
                                                                    validationMsg[i]
                                                                        .residence_since !== '',
                                                                helperText:
                                                                    validationMsg[i]
                                                                        .residence_since,
                                                            },
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                                <Autocomplete
                                                    id="Age"
                                                    value={String(item.age)}
                                                    options={ages}
                                                    onChange={(_, value: string | null) => {
                                                        handleUpdate(i, 'age', value);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={`*${t('age', {
                                                                lng: language,
                                                            })}`}
                                                            error={validationMsg[i].age !== ''}
                                                            helperText={validationMsg[i].age}
                                                        />
                                                    )}
                                                />
                                            </S.GridSection>
                                        </div>
                                    </S.FlexColumn>
                                </S.EntryCard>
                            );
                        })}
                    {hasRelative && (
                        <AddEntryFooter
                            title={t('addRelative', { lng: language })}
                            addLabel={t('addRelative', { lng: language })}
                            addAnotherLabel={t('addAnotherRelative', { lng: language })}
                            hasEntries={data.length > 0}
                            onClick={() => handleAddEntry()}
                        />
                    )}
                </FormBox>
            )}
        </>
    );
};

export default RelativesAbroadForm;
